import { useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { get } from 'lodash-es';
import dayjs from 'lib/dayjs';
import { isTiKTok } from 'lib/utils/isTikTok';
import { formatNumber, parseNumber } from 'utils/numberFormat';
import { toUpperCase } from 'utils/toLowerCase';
import { getCouponStatusLabel } from 'services/i18n/enum/coupon';

/** 状态标签：仅描述展示类型与 i18n key，不包含 JSX */
export interface VoucherCardTagInfo {
  status: 'ENDED' | 'SCHEDULED' | 'ONGOING';
  labelKey: string;
}

/** 优惠展示：纯文案与可选副文案 */
export interface VoucherCardDiscountInfo {
  kind: 1 | 2 | 3;
  mainText: string;
  subText?: string;
}

export interface UseVoucherCardDisplayParams {
  voucher: Conversation.Voucher.Vo;
}

export interface UseVoucherCardDisplayResult {
  /** 券名称、类型（供图标与 tooltip 使用） */
  voucherName: string;
  voucherType: 1 | 2;
  /** 状态标签：null 表示不展示 */
  tag: VoucherCardTagInfo | null;
  /** 优惠金额/比例展示：null 表示不展示 */
  discount: VoucherCardDiscountInfo | null;
  /** 有效期/领取后有效期的展示文案 */
  dateText: string;
  /** 是否展示「适用商品」按钮（商品券） */
  showProductPreviewer: boolean;
  /** 是否展示券码及复制 */
  showVoucherCode: boolean;
  voucherCode: string;
  /** 是否展示使用量（非 TikTok） */
  showUsageAmount: boolean;
  usageQuantity: number;
  currentUsage: number;
  /** 使用量是否高亮（用满） */
  usageAmountHighlight: boolean;
  /** 是否展示每人限领（LAZADA） */
  showPerCustomerLimit: boolean;
  perCustomerLimit: number;
  /** 是否展示领取开始时间（LAZADA） */
  showCollectStartTime: boolean;
  collectStartTimeFormatted: string;
  /** 是否展示最低消费 */
  showMinBasketPrice: boolean;
  minBasketPriceText: string;
  /** 是否展示最高优惠（折扣/比例券） */
  showMaxPriceDetail: boolean;
  maxPriceText: string;
  /** 是否展示券查看时间 */
  showDisplayStartTime: boolean;
  displayStartTimeFormatted: string;
  /** 是否已过期 */
  isExpired: boolean;
}

/**
 * 汇总 VoucherCard 各字段的显示规则与格式化结果，与 UI 解耦。
 * 组件仅根据返回的布尔与文案渲染，不包含 dayjs/formatNumber 等业务计算。
 */
export function useVoucherCardDisplay(
  params: UseVoucherCardDisplayParams,
): UseVoucherCardDisplayResult {
  const { voucher } = params;
  const { t } = useTranslate();

  const {
    platform,
    voucherName,
    voucherType,
    voucherStatus,
    suspendFlag,
    startTime,
    endTime,
    rewardType,
    discountAmount,
    percentage,
    unit,
    voucherCode,
    usageQuantity,
    currentUsage,
    perCustomerLimit,
    displayCollectStartTime,
    minBasketPrice,
    maxPrice,
    displayStartTime,
  } = voucher;

  const upperPlatform = toUpperCase(platform);

  const isTiktok = isTiKTok(upperPlatform);
  const isLazada = upperPlatform === 'LAZADA';

  const tag = useMemo((): VoucherCardTagInfo | null => {
    if (voucherStatus === 'ENDED') {
      return { status: 'ENDED', labelKey: getCouponStatusLabel(t, 'ENDED') };
    }
    if (voucherStatus === 'SCHEDULED') {
      return { status: 'SCHEDULED', labelKey: getCouponStatusLabel(t, 'SCHEDULED') };
    }
    if (voucherStatus === 'ONGOING') {
      return {
        status: 'ONGOING',
        labelKey: suspendFlag
          ? getCouponStatusLabel(t, 'SUSPENDED')
          : getCouponStatusLabel(t, 'ONGOING'),
      };
    }
    return null;
  }, [voucherStatus, t, suspendFlag]);

  const discount = useMemo((): VoucherCardDiscountInfo | null => {
    switch (rewardType) {
      case 1:
        return {
          kind: 1,
          mainText: `${unit} ${formatNumber(discountAmount, '0,0.00')}`,
        };
      case 2:
        return {
          kind: 2,
          mainText: formatNumber(Number(percentage) / 100, '0.00%'),
        };
      case 3:
        return {
          kind: 3,
          mainText: formatNumber(Number(percentage) / 100, '0.00%'),
          subText: 'Coins Cashback',
        };
      default:
        return null;
    }
  }, [rewardType, discountAmount, percentage, unit]);

  const dateText = useMemo(() => {
    if (isTiktok) {
      if (get(voucher, 'tiktokVoucherExtraInfo.redemptionDuration.type') === 'RELATIVE') {
        return t('chatWorkspace.couponTab.validityDays', {
          count: get(voucher, 'tiktokVoucherExtraInfo.redemptionDuration.relativeTime'),
        });
      }
      return `${dayjs(
        Number(get(voucher, 'tiktokVoucherExtraInfo.redemptionDuration.startTime')) * 1000,
      ).format('DD/MM/YYYY')}-${dayjs(
        Number(get(voucher, 'tiktokVoucherExtraInfo.redemptionDuration.endTime')) * 1000,
      ).format('DD/MM/YYYY')}`;
    }
    return `${dayjs(Number(startTime) * 1000).format('YYYY.MM.DD HH:mm:ss')} - ${dayjs(
      Number(endTime) * 1000,
    ).format('YYYY.MM.DD HH:mm:ss')}`;
  }, [isTiktok, voucher, startTime, endTime, t]);

  const showMaxPriceDetail = rewardType === 2 || rewardType === 3;

  const maxPriceText = useMemo(() => {
    if (!showMaxPriceDetail) return '';
    return parseNumber(maxPrice)
      ? `${unit} ${formatNumber(maxPrice, '0,0.00')}`
      : t('chatWorkspace.couponTab.maxDiscount.noLimit');
  }, [showMaxPriceDetail, maxPrice, unit, t]);

  const collectStartTimeFormatted = useMemo(() => {
    if (!displayCollectStartTime) return '';
    return dayjs(parseInt(displayCollectStartTime, 10) * 1000).format('YYYY.MM.DD HH:mm:ss');
  }, [displayCollectStartTime]);

  const displayStartTimeFormatted = useMemo(() => {
    if (!displayStartTime || displayStartTime === '0') return '';
    return dayjs(Number(displayStartTime) * 1000).format('YYYY.MM.DD HH:mm:ss');
  }, [displayStartTime]);

  const minBasketPriceText = useMemo(() => {
    if (!minBasketPrice) return '';
    return `${unit} ${formatNumber(minBasketPrice, '0,0.00')}`;
  }, [minBasketPrice, unit]);

  return {
    voucherName,
    voucherType: voucherType as 1 | 2,
    tag,
    discount,
    dateText,
    showProductPreviewer: voucherType === 2,
    showVoucherCode: Boolean(voucherCode),
    voucherCode: voucherCode ?? '',
    showUsageAmount: !isTiktok,
    usageQuantity,
    currentUsage,
    usageAmountHighlight: currentUsage === usageQuantity,
    showPerCustomerLimit: isLazada,
    perCustomerLimit,
    showCollectStartTime: Boolean(displayCollectStartTime) && isLazada,
    collectStartTimeFormatted,
    showMinBasketPrice: Boolean(minBasketPrice),
    minBasketPriceText,
    showMaxPriceDetail,
    maxPriceText,
    showDisplayStartTime: Boolean(displayStartTime) && displayStartTime !== '0',
    displayStartTimeFormatted,
    isExpired: voucherStatus === 'ENDED',
  };
}
